import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2, Mail, Send, AlertCircle, CheckCircle2, CreditCard, Wallet, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function SectionCard({ icon: Icon, color, title, subtitle, children, configured }) {
  return (
    <div className="card-gs mb-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: `${color}15`, color }}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-[#1E293B]">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {configured !== undefined && (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
            configured ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-600 border-slate-200"
          }`}>
            {configured ? <><CheckCircle2 className="w-3 h-3" /> Configurato</> : <><AlertCircle className="w-3 h-3" /> Non configurato</>}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function MaskedField({ label, value, current, onChange, placeholder, testId, type = "password" }) {
  return (
    <div>
      <label className="label-gs">{label}</label>
      <input data-testid={testId} type={type} value={value}
        onChange={(e) => onChange(e.target.value)} className="input-gs font-mono"
        placeholder={current || placeholder} />
      {current && !value && (
        <div className="text-[11px] text-slate-500 mt-1">Chiave attuale: <code className="text-[#2456C6]">{current}</code> — lascia vuoto per non modificare</div>
      )}
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Form state — only filled when user enters new values
  const [resend, setResend] = useState({ key: "", sender: "" });
  const [stripe, setStripe] = useState({ pub: "", secret: "", webhook: "", mode: "test" });
  const [paypal, setPaypal] = useState({ client_id: "", secret: "", mode: "sandbox" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setResend({ key: "", sender: data.sender_email || "onboarding@resend.dev" });
      setStripe({
        pub: data.stripe_publishable_key || "",
        secret: "",
        webhook: "",
        mode: data.stripe_mode || "test",
      });
      setPaypal({
        client_id: data.paypal_client_id || "",
        secret: "",
        mode: data.paypal_mode || "sandbox",
      });
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const saveSection = async (payload, label) => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", payload);
      toast.success(`${label} salvato`);
      await load();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      const { data } = await api.post("/admin/test-email", { to: testEmail });
      if (data.sent) toast.success(`Email inviata a ${testEmail}`);
      else toast.error(data.message || "Errore invio");
    } catch {
      toast.error("Errore invio");
    } finally { setTesting(false); }
  };

  if (!settings) return <DashboardLayout role="superadmin"><div className="p-8"><Loader2 className="w-8 h-8 animate-spin text-[#2456C6]" /></div></DashboardLayout>;

  return (
    <DashboardLayout role="superadmin">
      <div className="p-4 sm:p-8 max-w-3xl">
        <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-1">Impostazioni globali</h1>
        <p className="text-sm text-slate-500 mb-6">Configurazione servizi di sistema (email + pagamenti)</p>

        {/* RESEND */}
        <SectionCard icon={Mail} color="#F77F2A" title="Servizio email (Resend)"
          subtitle="Notifiche, OTP firma, conferma pratica"
          configured={!!settings.resend_api_key_masked}>
          <div className="space-y-4">
            <MaskedField label="API Key Resend" value={resend.key}
              current={settings.resend_api_key_masked}
              onChange={(v) => setResend({ ...resend, key: v })}
              placeholder="re_xxxxxxxxxxxxx" testId="resend-key-input" />
            <div className="text-xs text-slate-500 -mt-2">
              Ottieni la chiave su <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer"
                className="text-[#2456C6] font-bold inline-flex items-center gap-1">resend.com → API Keys <ExternalLink className="w-3 h-3" /></a>
            </div>
            <div>
              <label className="label-gs">Email mittente</label>
              <input data-testid="sender-email-input" type="email" value={resend.sender}
                onChange={(e) => setResend({ ...resend, sender: e.target.value })} className="input-gs"
                placeholder="noreply@gostreet.it" />
              <div className="text-xs text-slate-500 mt-1">In sandbox: <code>onboarding@resend.dev</code>. Per produzione verifica il dominio.</div>
            </div>
            <button onClick={() => {
              const payload = { sender_email: resend.sender };
              if (resend.key) payload.resend_api_key = resend.key;
              saveSection(payload, "Resend");
              setResend({ ...resend, key: "" });
            }} disabled={saving} data-testid="save-resend-button"
              className="btn-gs-primary disabled:opacity-50">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva email"}
            </button>
          </div>
        </SectionCard>

        {/* TEST EMAIL */}
        <SectionCard icon={Send} color="#2456C6" title="Test email"
          subtitle="Verifica la corretta configurazione Resend">
          <div className="flex gap-2">
            <input data-testid="test-email-input" type="email" value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)} className="input-gs flex-1"
              placeholder="indirizzo@email.com" />
            <button onClick={sendTest} disabled={testing || !testEmail} data-testid="send-test-button"
              className="btn-gs-secondary !py-3 disabled:opacity-50">
              {testing ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia test"}
            </button>
          </div>
        </SectionCard>

        {/* STRIPE */}
        <SectionCard icon={CreditCard} color="#635BFF" title="Pagamenti · Stripe"
          subtitle="Carte di credito, Apple Pay, Google Pay, SEPA"
          configured={!!settings.stripe_secret_key_masked}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setStripe({ ...stripe, mode: "test" })}
                data-testid="stripe-mode-test"
                className={`py-2 rounded-lg text-sm font-bold border-2 ${
                  stripe.mode === "test" ? "border-[#635BFF] bg-[#635BFF]/5 text-[#635BFF]" : "border-slate-200 text-slate-600"
                }`}>TEST</button>
              <button type="button" onClick={() => setStripe({ ...stripe, mode: "live" })}
                data-testid="stripe-mode-live"
                className={`py-2 rounded-lg text-sm font-bold border-2 ${
                  stripe.mode === "live" ? "border-green-600 bg-green-50 text-green-700" : "border-slate-200 text-slate-600"
                }`}>LIVE</button>
            </div>
            <div>
              <label className="label-gs">Publishable Key</label>
              <input data-testid="stripe-publishable-input" type="text" value={stripe.pub}
                onChange={(e) => setStripe({ ...stripe, pub: e.target.value })} className="input-gs font-mono"
                placeholder={stripe.mode === "test" ? "pk_test_..." : "pk_live_..."} />
              <div className="text-[11px] text-slate-500 mt-1">Pubblica · esposta al frontend</div>
            </div>
            <MaskedField label="Secret Key" value={stripe.secret}
              current={settings.stripe_secret_key_masked}
              onChange={(v) => setStripe({ ...stripe, secret: v })}
              placeholder={stripe.mode === "test" ? "sk_test_..." : "sk_live_..."}
              testId="stripe-secret-input" />
            <MaskedField label="Webhook Signing Secret" value={stripe.webhook}
              current={settings.stripe_webhook_secret_masked}
              onChange={(v) => setStripe({ ...stripe, webhook: v })}
              placeholder="whsec_..." testId="stripe-webhook-input" />
            <div className="text-xs text-slate-500">
              Ottieni le chiavi su <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer"
                className="text-[#2456C6] font-bold inline-flex items-center gap-1">dashboard.stripe.com → API keys <ExternalLink className="w-3 h-3" /></a>
            </div>
            <button onClick={() => {
              const payload = { stripe_mode: stripe.mode, stripe_publishable_key: stripe.pub };
              if (stripe.secret) payload.stripe_secret_key = stripe.secret;
              if (stripe.webhook) payload.stripe_webhook_secret = stripe.webhook;
              saveSection(payload, "Stripe");
              setStripe({ ...stripe, secret: "", webhook: "" });
            }} disabled={saving} data-testid="save-stripe-button"
              className="btn-gs-primary disabled:opacity-50">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva Stripe"}
            </button>
          </div>
        </SectionCard>

        {/* PAYPAL */}
        <SectionCard icon={Wallet} color="#003087" title="Pagamenti · PayPal"
          subtitle="Pagamenti con account PayPal o carta via PayPal Checkout"
          configured={!!settings.paypal_client_secret_masked}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setPaypal({ ...paypal, mode: "sandbox" })}
                data-testid="paypal-mode-sandbox"
                className={`py-2 rounded-lg text-sm font-bold border-2 ${
                  paypal.mode === "sandbox" ? "border-[#003087] bg-[#003087]/5 text-[#003087]" : "border-slate-200 text-slate-600"
                }`}>SANDBOX</button>
              <button type="button" onClick={() => setPaypal({ ...paypal, mode: "live" })}
                data-testid="paypal-mode-live"
                className={`py-2 rounded-lg text-sm font-bold border-2 ${
                  paypal.mode === "live" ? "border-green-600 bg-green-50 text-green-700" : "border-slate-200 text-slate-600"
                }`}>LIVE</button>
            </div>
            <div>
              <label className="label-gs">Client ID</label>
              <input data-testid="paypal-client-id-input" type="text" value={paypal.client_id}
                onChange={(e) => setPaypal({ ...paypal, client_id: e.target.value })} className="input-gs font-mono"
                placeholder="A21AA..." />
            </div>
            <MaskedField label="Client Secret" value={paypal.secret}
              current={settings.paypal_client_secret_masked}
              onChange={(v) => setPaypal({ ...paypal, secret: v })}
              placeholder="EL2t..." testId="paypal-secret-input" />
            <div className="text-xs text-slate-500">
              Ottieni le credenziali su <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer"
                className="text-[#2456C6] font-bold inline-flex items-center gap-1">developer.paypal.com → Apps <ExternalLink className="w-3 h-3" /></a>
            </div>
            <button onClick={() => {
              const payload = { paypal_mode: paypal.mode, paypal_client_id: paypal.client_id };
              if (paypal.secret) payload.paypal_client_secret = paypal.secret;
              saveSection(payload, "PayPal");
              setPaypal({ ...paypal, secret: "" });
            }} disabled={saving} data-testid="save-paypal-button"
              className="btn-gs-primary disabled:opacity-50">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva PayPal"}
            </button>
          </div>
        </SectionCard>

        <div className="card-gs bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <b>Nota:</b> attualmente queste caselle salvano le chiavi nel database (cifrate dal sistema operativo).
              L'integrazione effettiva di checkout Stripe/PayPal con piani abbonamento (Starter €29, Pro €59, Studio €129)
              verrà implementata in una iterazione successiva. Le chiavi sono già pronte e disponibili al backend per quel momento.
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
